
import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../lib/api";
import { useTenant } from "../lib/tenant";

type DimensionScore = {
  dimension: "completeness" | "accuracy" | "consistency" | "timeliness" | "validity";
  score: number;
  weight: number;
  note: string;
  issues: string[];
};

type ColumnMapping = {
  source_column: string;
  suggested_role: string;
  confidence: number;
};

type QualityReport = {
  source_id: string;
  tenant_id: string;
  overall_score: number;
  grade: "A" | "B" | "C" | "D" | "F";
  dimensions: DimensionScore[];
  column_mappings: ColumnMapping[];
  blocks_kpi_display: boolean;
  generated_at: string;
  run_id: string;
};

const DIM_LABEL: Record<DimensionScore["dimension"], string> = {
  completeness: "Vollständigkeit",
  accuracy: "Genauigkeit",
  consistency: "Konsistenz",
  timeliness: "Aktualität",
  validity: "Validität",
};

function gradeClass(grade: string): string {
  if (grade === "A") return "grade-a";
  if (grade === "B") return "grade-b";
  if (grade === "C") return "grade-c";
  if (grade === "D") return "grade-d";
  return "grade-f";
}

export default function SourceQualityPage() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const api = useApi();
  const { effectiveTenantId } = useTenant();
  const qc = useQueryClient();

  const report = useQuery({
    queryKey: ["quality", effectiveTenantId, sourceId],
    queryFn: () => api.get<QualityReport>(`/sources/${sourceId}/quality`),
    enabled: !!sourceId,
  });

  const recheck = useMutation({
    mutationFn: () => api.post<QualityReport>(`/sources/${sourceId}/quality-check`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quality", effectiveTenantId, sourceId] }),
  });

  if (!sourceId) return <section className="quality-page"><p>Quelle nicht gefunden.</p></section>;
  if (report.isLoading) return <section className="quality-page"><p className="muted">Lade Quality-Report …</p></section>;
  if (report.error) return <section className="quality-page"><p className="error">Fehler: {String(report.error)}</p></section>;
  if (!report.data) return null;

  const r = report.data;
  return (
    <section className="quality-page">
      <header className="quality-hero">
        <div>
          <h1>Daten-Qualität · {sourceId}</h1>
          <p className="muted small">
            Stand: {new Date(r.generated_at).toLocaleString()} · run_id <code>{r.run_id}</code>
          </p>
        </div>
        <div className={`grade-badge ${gradeClass(r.grade)}`}>
          <div className="grade-letter">{r.grade}</div>
          <div className="grade-score">{r.overall_score} / 100</div>
        </div>
      </header>

      {r.blocks_kpi_display && (
        <div className="quality-warn">
          ⚠️ Datenqualität unter Schwellwert <strong>60</strong> — KPI-Anzeige für diese Quelle ist
          gesperrt. Bitte beheben Sie die identifizierten Issues oder laden Sie verbesserte Daten hoch.
        </div>
      )}

      <section className="quality-dims">
        <h2>5 Dimensionen</h2>
        <div className="dim-grid">
          {r.dimensions.map((d) => (
            <div key={d.dimension} className="dim-card">
              <div className="dim-head">
                <strong>{DIM_LABEL[d.dimension]}</strong>
                <span className="muted small">Gewicht {(d.weight * 100).toFixed(0)}%</span>
              </div>
              <div className="dim-bar">
                <div className="dim-fill" style={{ width: `${d.score}%` }} />
                <span className="dim-score">{d.score}</span>
              </div>
              <p className="dim-note">{d.note}</p>
              {d.issues.length > 0 && (
                <ul className="dim-issues">
                  {d.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="quality-cols">
        <h2>KI-Spalten-Mapping</h2>
        <p className="muted">
          Auto-Detection erkennt typische Spalten-Bedeutungen. Sie können das Mapping
          beim Discovery-Run überschreiben.
        </p>
        <table className="cols-table">
          <thead>
            <tr><th>Spalte</th><th>Erkannte Bedeutung</th><th>Konfidenz</th></tr>
          </thead>
          <tbody>
            {r.column_mappings.map((c, i) => (
              <tr key={i}>
                <td><code>{c.source_column}</code></td>
                <td>{c.suggested_role}</td>
                <td>
                  <span className={`conf ${c.confidence >= 90 ? "high" : c.confidence >= 80 ? "med" : "low"}`}>
                    {c.confidence}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <div className="quality-actions">
        <button className="btn primary" onClick={() => recheck.mutate()} disabled={recheck.isPending}>
          {recheck.isPending ? "Prüfe …" : "Quality-Check neu starten"}
        </button>
        <Link to="/app/sources" className="btn ghost">Zurück zu Quellen</Link>
      </div>
    </section>
  );
}
